import type { Locale, Money, PaymentMethod, Visit } from '@360face/domain';

/**
 * Therapist tablet texts: PT, EN, RU (owner, 14.10.2026). The tablet has its own
 * language list — the customer kiosk stays PT/EN. Service names come from the CRM
 * catalogue, which has PT/EN only; Russian shows the English names.
 */
const pt = {
  langName: 'PT',
  alert: (mins: number) => `Um cliente pediu ajuda no quiosque${mins > 0 ? ` há ${mins} min` : ''}`,
  alertGiftEmail: 'Novo pedido de certificado por e-mail — ver a caixa info@360face.pt',
  deliveryEmail: 'Por e-mail',
  deliveryPickup: 'Levantar no estúdio',
  clientComment: 'Comentário da cliente',
  deliveryEmailNote: 'Pedido de certificado por e-mail — tratado pela receção através de info@360face.pt',
  cardFor: 'Para',
  cardFrom: 'De',
  mailStatus: {
    sent: 'Pedido enviado para o e-mail do estúdio e confirmação ao cliente',
    failed: 'E-mail não enviado (erro) — contactar o cliente à mão',
    off: 'E-mail do servidor ainda não configurado — contactar o cliente à mão',
  },
  alertGo: 'Vou já',
  alertVideo: 'Cliente precisa de ajuda no quiosque — o operador não atendeu',
  alertOperator: 'O operador pede uma terapeuta no quiosque',
  kindProduct: 'Produto',
  kindGift: 'Certificado',
  desk: 'Receção',
  sale: 'Venda',
  crmSyncOk: 'Serviços realizados registados no Altegio',
  crmSyncFailed: 'Altegio não atualizado — corrigir à mão',
  nif: 'NIF para a fatura (opcional)',
  formOk: 'Ficha confirmada',
  formNeeded: 'Dar ficha ao cliente',
  formSubmitted: 'Ficha preenchida no quiosque',
  formOpen: 'Ver ficha de saúde',
  formHide: 'Ocultar ficha',
  formNone: 'Não há ficha preenchida no quiosque para este cliente.',
  formFilled: (day: string) => `Preenchida em ${day}`,
  formEarlier: 'de uma visita anterior',
  formAttention: (n: number) =>
    `Atenção: ${n} ${n === 1 ? 'resposta «sim»' : 'respostas «sim»'} — verificar antes do tratamento`,
  formAllNo: 'Todas as respostas «não».',
  cardRegular: 'Cliente habitual',
  cardNew: 'Primeira visita',
  cardNewHint: 'Explique o tratamento, mostre o resultado e convide para voltar.',
  cardVisits: (n: number, min: boolean, since?: string) =>
    `${n}${min ? '+' : ''} ${n === 1 ? 'visita' : 'visitas'}${since ? ` desde ${since}` : ''}`,
  cardLast: 'Últimos tratamentos',
  cardNotes: 'Notas',
  cardPressure: 'Pressão preferida',
  cardNoHistory: 'Histórico do Altegio indisponível agora',
  cardClientNote: 'Notas sobre o cliente (Altegio)',
  cardShowAll: (n: number) => `Ver todas as notas (${n})`,
  cardShowLess: 'Mostrar menos',
  cardAddNote: '+ Nota sobre o cliente',
  cardAddNoteHint: 'Fica no fim da nota do cliente no Altegio. Só a equipa vê; o cliente não recebe.',
  cardAddNoteSave: 'Guardar nota',
  cardAddNoteCancel: 'Cancelar',
  cardAddNoteError: 'Não foi possível guardar a nota. Tente de novo ou peça ao administrador.',
  // treatment protocol (owner 2.11.2026)
  // protocol history, repeat, paper protocol (owner 04.11.2026)
  protoHistory: 'Histórico de protocolos',
  protoHistoryEmpty: 'Ainda sem protocolos anteriores.',
  protoRepeat: '↺ Como da última vez',
  protoRepeatFrom: (date: string, who?: string) => `Copiado do protocolo de ${date}${who ? ` · ${who}` : ''}`,
  protoPaper: '+ Protocolo em papel',
  protoPaperTitle: 'Protocolo em papel (visita anterior)',
  protoPaperDate: 'Data da visita',
  protoPaperWho: 'Terapeuta (do papel)',
  protoPaperBadge: 'papel',
  protoPaperNote: 'Fica só no cartão do cliente. Não é enviado ao cliente.',
  protoPaperNeed: 'Escolha a data e pelo menos um item',
  protoPaperRemove: 'Remover',
  protoPaperRemoveAsk: 'Remover este protocolo em papel?',
  protoComment: 'Comentário',
  // hot windows (owner 04.11.2026)
  hotOpen: '🔥 Horários livres',
  hotTitle: 'Horários livres de hoje → notificação',
  hotService: 'Tratamento',
  hotTimes: 'Escolha até 4 horários',
  hotNone: 'Hoje já não há horários livres para este tratamento.',
  hotAudience: (w: number, all: number) =>
    `Recebem: ${w} ${w === 1 ? 'cliente' : 'clientes'}${all > w ? ` (${all - w} já receberam hoje)` : ''}`,
  hotPreview: 'O cliente vê',
  hotOff: 'As notificações ainda não estão ligadas no servidor (faltam as chaves Apple / Firebase).',
  hotSend: 'Enviar notificação',
  hotSent: (n: number) => `Enviada a ${n} ${n === 1 ? 'telemóvel' : 'telemóveis'} ✓`,
  hotRule: 'Só a clientes que pediram «horários livres» na aplicação. No máximo uma vez por dia.',
  recoPush: '📲 Enviar para a aplicação',
  recoPushSent: (n: number) => `Notificação enviada (${n}) ✓`,
  recoPushAlready: 'Já enviada para a aplicação ✓',
  recoPushNone: 'O cliente não tem a aplicação com notificações ligadas.',
  protoPick: 'Escolher…',
  protoButton: 'Protocolo e recomendações',
  protoTitle: 'Protocolo do tratamento',
  protoStudio: 'Para o estúdio (o cliente não vê)',
  protoClient: 'Para o cliente',
  protoMask: 'Máscara',
  protoAmpoule: 'Ampola',
  protoOther: 'Outro',
  protoType: 'Tipo de tratamento',
  protoOwn: 'A sua recomendação (opcional)',
  protoNeed: 'Escolha algo para o cliente ou escreva uma recomendação',
  recoButton: 'Recomendações para o cliente',
  recoTitle: 'Recomendações para o cliente',
  recoHint: 'Pode preencher no fim do turno. O cliente recebe-as por e-mail amanhã às 11:00.',
  recoText: 'Recomendações (cuidados em casa, o que reparou, o que fazer na próxima vez)',
  recoProducts: 'Produtos recomendados',
  recoWeeks: 'Voltar dentro de',
  recoWeeksN: (n: number) => `${n} sem.`,
  recoSend: 'Enviar ao cliente por e-mail amanhã às 11:00',
  recoNoEmail: 'O cliente não tem e-mail — fica só como nota para as terapeutas',
  recoSave: 'Guardar',
  recoEdit: 'Editar',
  recoMissing: 'Sem recomendações',
  recoMissingCount: (n: number) => `Recomendações por preencher: ${n}`,
  recoStatus: {
    scheduled: 'Será enviada por e-mail amanhã às 11:00',
    sent: 'Enviada ao cliente',
    failed: 'Erro no envio — tentar mais tarde',
    no_email: 'Sem e-mail — guardada como nota',
    not_sent: 'Só nota (não enviada)',
    expired: 'Não enviada (prazo passou)',
  } as Record<string, string>,
  formAlert: '⚠ O cliente assinalou algo na ficha — abrir antes do tratamento',
  formNoneDeclared: 'Contraindicações: nenhuma (confirmado pelo cliente)',
  formIpadDeclared: 'Ficha do iPad: o cliente indicou que algo da lista se aplica — pergunte o quê antes do tratamento',
  formIpadCleared:
    'Ficha do iPad: algo da lista se aplica, mas o cliente tem autorização médica — confirme antes do tratamento',
  formIpadAesthetic:
    'Procedimentos estéticos recentes (botox, preenchimentos, fios ou cirurgia) — pergunte zonas e datas',
  formFromIpad: 'preenchida no iPad',
  formDeclared: 'O cliente assinalou:',
  formAesthetic: 'Procedimentos estéticos (6 meses):',
  formOther: 'Outras notas:',
  formDetailDeleted: 'O cliente assinalou algo nesse dia — os detalhes já foram apagados (só no próprio dia)',
  formDeletesAt: 'Os detalhes de saúde são apagados hoje à meia-noite',
  formBirth: 'Nascimento',
  formPressure: 'Pressão',
  formContact: 'Contacto',
  formHeard: 'Conheceu por',
  formSimilar: 'Já fez tratamentos semelhantes',
  formSignature: 'Assinatura',
  formDecide: 'Decisão da terapeuta',
  formProceed: 'Fazer o tratamento',
  formDecline: 'Não fazer',
  formDeclineConfirm:
    'Não fazer o tratamento? A visita fecha sem serviços e sem pagamento, com a nota «indicação médica — decisão da terapeuta».',
  formDeclineYes: 'Sim, fechar sem tratamento',
  formDeclineCancel: 'Voltar',
  medicalDeclined: 'Recusado por indicação médica — decisão da terapeuta. Visita fechada sem serviços, 0 €.',
  medicalDeclineCrm: (s: 'ok' | 'failed' | 'manual'): string =>
    s === 'ok'
      ? 'Altegio: 0 € e nota registados'
      : s === 'failed'
        ? 'Altegio: não atualizado — marque à mão'
        : 'Altegio: marque à mão (0 €, indicação médica)',
  formDecided: (d: 'proceed' | 'decline'): string =>
    d === 'proceed' ? 'Decisão: fazer o tratamento' : 'Decisão: não fazer',
  contactLabels: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Chamada' } as Record<
    string,
    string
  >,
  heardLabels: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'Assistente de IA (ChatGPT, Claude…)',
    friend: 'Recomendação',
    classpass: 'ClassPass',
    passing_by: 'Passou pelo estúdio',
    other: 'Outro',
  } as Record<string, string>,
  formYes: 'Sim',
  formNo: 'Não',
  formDraft: 'Perguntas provisórias',
  formLogged: 'Dados de saúde — cada consulta fica registada. Não copiar nem fotografar.',
  saleInfo: 'Venda no quiosque — registar no Altegio depois do pagamento',
  handOver: 'Entregar certificado',
  giftCode: 'Nº do certificado — leia o código de barras',
  presentLabel: 'Presente da casa',
  presentPrepare: (gift: string) => `Preparar: ${gift}`,
  giftHandedTakeHome: 'Entreguei o presente à cliente',
  giftHandedDone: 'Fiz o presente à cliente',
  giftHandedFirst: 'Entregue o presente e marque — sem isso a visita não pode ser terminada.',
  giftHandedDoneShort: 'entregue ✓',
  nextTitle: 'À saída',
  nextRebook: 'Marcar de novo',
  nextRebookSub: 'Horários livres desta terapeuta',
  nextProducts: 'Produtos',
  nextProductsSub: 'Para continuar em casa',
  nextTreatment: 'Tratamento',
  nextNoDates: 'Sem horários livres nos próximos dias — marcar na receção.',
  nextPickTime: 'Escolha o dia e a hora',
  nextConfirm: (when: string) => `Marcar para ${when}`,
  nextBooked: (when: string) => `Próxima visita marcada: ${when}`,
  nextRequested: 'Pedido registado — introduzir no Altegio',
  nextInCrm: 'Marcado no Altegio',
  nextAdd: 'Adicionar',
  nextAdded: 'Na conta',
  nextSkip: 'Agora não',
  pinTitle: 'Introduza o PIN',
  enter: 'Entrar',
  logout: 'Sair',
  today: 'Hoje',
  tomorrow: 'Amanhã',
  prevDay: 'Dia anterior',
  nextDay: 'Dia seguinte',
  all: 'Todas',
  visitsCount: (n: number) => `${n} ${n === 1 ? 'visita' : 'visitas'}`,
  empty: 'Sem visitas neste dia.',
  back: '← Lista',
  states: {
    booked: 'Marcado',
    checked_in: 'Chegou',
    in_service: 'Em tratamento',
    completed: 'Concluído — por pagar',
    closed: 'Pago e fechado',
    cancelled: 'Cancelado',
    no_show: 'Não compareceu',
  } satisfies Record<Visit['state'], string>,
  booking: {
    requested: 'Pedido do quiosque — introduzir no Altegio',
    crm_booked: 'Criado no Altegio',
    from_crm: 'Marcação do Altegio',
  } satisfies Record<Visit['bookingStatus'], string>,
  methods: {
    card_terminal: 'Cartão',
    mbway: 'MB WAY',
    cash: 'Numerário',
    bank_transfer: 'Transferência',
    gift_card: 'Certificado',
    qr_transfer: 'QR (Wise/bunq/Revolut)',
  } satisfies Record<PaymentMethod, string>,
  giftLookup: 'Verificar',
  giftValue: (value: string, balance: string) => `Valor ${value} · saldo ${balance}`,
  giftCoversAll: (left: string) => `O certificado cobre o total. Fica no certificado: ${left}.`,
  giftPartial: (covers: string, missing: string) =>
    `O certificado cobre ${covers}, faltam ${missing} — escolha como o cliente paga o resto.`,
  giftUsedUp: 'Este certificado já foi totalmente utilizado (saldo 0).',
  giftNotFound: 'Certificado não encontrado.',
  giftRegister: 'Registar este certificado',
  giftRegisterValue: 'Valor do certificado (€)',
  giftRegisterHint: 'Para certificados antigos ou em papel. Introduza o PIN abaixo.',
  preferredPayment: 'Pagamento preferido',
  arrived: 'Cliente chegou',
  start: 'Iniciar tratamento',
  noShow: 'Não compareceu',
  cancel: 'Cancelar',
  finish: 'Concluir tratamento',
  performedTitle: 'O que foi realizado',
  addService: '+ Acrescentar extra',
  noTime: (extra: number, free: number, next: string) =>
    `Atenção: +${extra} min, mas só há ${free} min livres antes da marcação das ${next} (com pausa).`,
  toPay: 'A pagar',
  save: 'Guardar e concluir',
  payTitle: 'Pagamento recebido',
  payHint: 'Confirme só depois de receber o dinheiro. Nenhum pagamento é processado aqui.',
  pin: 'PIN',
  confirmPay: 'Confirmar pagamento',
  ordered: 'Pedido',
  performed: 'Feito',
  paid: 'Pago',
  total: 'Total',
  diff: {
    ordered_not_performed: 'pedido e não feito',
    performed_not_ordered: 'feito sem pedido',
    performed_not_paid: 'feito e não pago',
    paid_not_performed: 'pago e não feito',
  },
  certs: 'Certificados',
  certsTitle: 'Certificados de oferta',
  certsAltegio: 'Registar no Altegio',
  certsAltegioHint:
    'O Altegio não aceita vendas de certificados pela API. Repita cada linha no Altegio e marque «Registado».',
  certsAltegioEmpty: 'Tudo registado no Altegio.',
  certsSale: 'Venda',
  certsUse: 'Pagamento',
  certsSaleHow: 'Altegio: vender «Gift card» deste valor, código = este número, associar ao cliente.',
  certsUseHow: 'Altegio: na visita → Split payment → «Gift card» → este número → este valor.',
  certsBalance: (m: string) => `saldo ${m}`,
  certsUntil: (d: string) => `válido até ${d}`,
  certsMarked: 'Registado no Altegio',
  certsStock: 'Em stock (impressos)',
  certsValue: 'Valor',
  certsInStock: 'Na prateleira',
  certsSold: 'Vendidos',
  certsNewBatch: 'Imprimir novos números',
  certsCount: 'Quantos de cada valor',
  certsCreate: 'Criar números',
  certsBatches: 'Lotes',
  certsPrint: 'Imprimir',
  certsPrintNow: 'Imprimir / guardar PDF',
  certsBatchLeft: (n: number) => `${n} por vender`,
  certsAltegioStockHint: (n: number) =>
    `No Altegio: tipo de gift card por valor (uso múltiplo, 12 meses após a venda) e ${n} unidades de cada em stock.`,
  printGift: 'Certificado de oferta',
  printGiftEn: 'Gift certificate',
  printValid: (months: number) =>
    `Válido ${months} meses a partir da data de compra · Valid ${months} months from purchase`,
  printFor: 'Para / For',
  printFrom: 'De / From',
  printNo: 'N.º',
  giftStock: 'Este número ainda não foi vendido.',
  giftUntil: (d: string) => `Válido até ${d}.`,
  tipTitle: 'Gorjeta',
  tipHint: 'Se o cliente deixou gorjeta (por exemplo, no terminal), indique o valor.',
  tipAmount: 'Valor da gorjeta, €',
  discountTitle: 'Desconto',
  discountAmount: 'Outro valor, €',
  discountReason: 'Motivo (obrigatório)',
  discountReasonPick: '— escolha o motivo —',
  discountNote: 'Comentário',
  discountNoteRequired: 'Comentário (obrigatório)',
  discountNone: 'Sem desconto',
  discountTooHigh: 'O desconto não pode ser maior do que a conta.',
  reasons: {
    therapist_late: 'Atraso da terapeuta',
    shorter_service: 'Serviço mais curto',
    therapist_fault: 'Por culpa da terapeuta',
    management: 'A pedido da direção',
    voucher: 'Voucher',
    other: 'Outro motivo',
  } as Record<string, string>,
  payBill: 'Conta',
  payDue: 'A cobrar',
  payTip: 'Gorjeta',
  payCharge: 'Total a receber',
  discountCrm: {
    ok: 'no Altegio ✓',
    failed: 'Altegio: erro — registar à mão',
    manual: 'registar à mão no Altegio',
  } as Record<string, string>,
  report: 'Relatório',
  reportTitle: (d: string) => `Relatório do dia ${d}`,
  reportDiscounts: 'Descontos',
  reportTips: 'Gorjetas',
  reportNone: 'Nada neste dia.',
  reportByReason: 'Por motivo',
  changeAmount: 'Alterar valor',
  adjustNone: 'Sem alteração',
  adjustDiscount: 'Desconto',
  adjustUpgrade: 'Upgrade',
  finalAmount: 'Valor final, €',
  upgradeHint: 'Indique o valor que o cliente paga em vez da conta.',
  payFinal: 'Valor final',
  reportUpgrades: 'Upgrades (valor final)',
  changeTreatment: 'Alterar tratamento',
  addExtras: 'Adicionar extras',
  addExtraAfter: 'Adicionar extra',
  pickTreatment: (from: string) => `Em vez de «${from}»:`,
  pickExtras: 'Escolha os extras:',
  applyChange: 'Aplicar',
  closePicker: 'Fechar',
  changedTo: 'alterado para',
  extraTime: (min: number) => `+${min} min`,
  shopButton: 'Vender produto',
  shopSub: 'Mostrar e vender cosmética',
  shopTitle: 'Vender produto',
  shopSearch: 'Procurar por nome ou ler o código de barras',
  shopInStock: (n: number) => `em stock: ${n}`,
  shopCart: 'Cesto',
  shopCartEmpty: 'Toque num produto ou leia o código de barras.',
  shopChangePrice: 'Alterar preço',
  shopNewPrice: 'Novo preço, €',
  shopMinPrice: (m: string) => `mínimo ${m} sem o administrador`,
  shopReason: 'Motivo / nome do cliente',
  shopReasonRequired: 'Motivo / nome do cliente (obrigatório com desconto)',
  shopSeller: 'Quem vende',
  shopMethod: 'Como pagou o cliente',
  shopFee: (p: string) => `+${p} % da forma de pagamento`,
  shopTotal: 'A cobrar',
  shopSold: 'Vendido',
  shopConfirmTitle: 'Confirmar a venda',
  shopConfirmBack: 'Voltar',
  shopDoneTitle: 'Feito',
  shopDoneText: (m: string) => `Passe ao administrador: emitir fatura de ${m}.`,
  shopNewSale: 'Nova venda',
  shopNotFound: (code: string) => `Código ${code} não encontrado.`,
  shopDetails: 'Detalhes',
  shopAdd: 'Adicionar',
  shopOverLimit: 'Desconto acima do limite — só com o administrador.',
  shopRejectedTitle: 'A venda não foi aceite pelo sistema de contas:',
  nextShopProducts: 'Produtos para venda',
  nextShopProductsSub: 'Do stock do estúdio · pago com o tratamento',
  nextShopEmpty: 'Sem produtos em stock neste estúdio.',
  shopSaleOk: (m: string) => `Produtos registados no sistema de contas (${m}). Emitir fatura.`,
  shopSaleFailed: 'Produtos ainda não registados no sistema de contas.',
  shopSaleManual: 'Pago com certificado — registar os produtos à mão no sistema de contas.',
  shopSaleRetry: 'Tentar de novo',
  errors: {
    NOTE_ONLY: 'Marcado como «só nota» — não é enviado.',
    PUSH_DISABLED: 'As notificações ainda não estão ligadas.',
    NOT_IN_STOCK: 'Não há mais em stock.',
    PRODUCT_UNAVAILABLE: 'Produto indisponível.',
    UCHET_UNAVAILABLE: 'O sistema de contas não responde. O cesto fica guardado — tente de novo.',
    UCHET_AUTH: 'O sistema de contas recusou o acesso. Avise o administrador.',
    UCHET_DISABLED: 'A venda de produtos não está ligada.',
    UCHET_REJECTED: 'A venda não foi aceite.',
    SELLER_UNKNOWN: 'Esta terapeuta não está ligada no sistema de contas.',
    METHOD_UNKNOWN: 'Forma de pagamento não configurada.',
    DISCOUNT_REASON_REQUIRED: 'Indique o motivo do desconto.',
    DISCOUNT_OVER_LIMIT: 'Desconto acima do limite — só com o administrador.',
    CHANGE_ONLY_BEFORE_FINISH: 'O tratamento só pode ser alterado antes de concluir.',
    UNKNOWN_SERVICE: 'Serviço indisponível.',
    ONE_AMOUNT_CHANGE: 'Escolha desconto ou upgrade, não os dois.',
    DISCOUNT_TOO_HIGH: 'O desconto não pode ser maior do que a conta.',
    DISCOUNT_NOT_ALLOWED: 'Um certificado é vendido pelo valor total, sem desconto.',
    GIFT_CODE_INVALID: 'Número errado — verifique os dígitos.',
    GIFT_VALUE_MISMATCH: 'Este número é de outro valor.',
    GIFT_NOT_IN_STOCK: 'Este número não foi impresso pelo Hub — verifique os dígitos.',
    GIFT_NOT_SOLD: 'Este certificado ainda não foi vendido.',
    GIFT_EXPIRED: 'Este certificado expirou.',
    UNKNOWN_GIFT_VALUE: 'Valor não previsto para este estúdio.',
    PIN_INVALID: 'PIN errado.',
    PIN_LOCKED: 'Demasiadas tentativas. Aguarde 15 minutos.',
    TABLET_DISABLED: 'O tablet ainda não está ativado no servidor.',
    INVALID_TRANSITION: 'Esta ação não é possível no estado atual.',
    CONCURRENT_UPDATE: 'Outro dispositivo alterou esta visita. Atualize.',
    NETWORK: 'Sem ligação. Tente novamente.',
    GIFT_CODE_REQUIRED: 'Indique o número do certificado.',
    GIFT_NOT_FOUND: 'Certificado não encontrado.',
    GIFT_USED_UP: 'Este certificado já foi totalmente utilizado.',
    GIFT_CODE_EXISTS: 'Este número de certificado já existe.',
    GIFT_ONE_PER_CODE: 'Um número = um certificado. Registe cada envelope separadamente.',
    REMAINDER_METHOD_REQUIRED: 'O certificado não cobre tudo — escolha como pagar o resto.',
    QUESTIONNAIRE_UNREADABLE: 'A ficha não pode ser lida (chave do servidor). Peça ao cliente a ficha em papel.',
    default: 'Erro. Tente novamente.',
  } as Record<string, string>,
};

export type TabletMessages = typeof pt;

const en: TabletMessages = {
  langName: 'EN',
  alert: (mins: number) => `A client asked for help at the kiosk${mins > 0 ? ` ${mins} min ago` : ''}`,
  alertGiftEmail: 'New gift certificate request by e-mail — see the info@360face.pt inbox',
  deliveryEmail: 'By e-mail',
  deliveryPickup: 'Pick up in the studio',
  clientComment: 'Client comment',
  deliveryEmailNote: 'Certificate requested by e-mail — handled by the desk via info@360face.pt',
  cardFor: 'For',
  cardFrom: 'From',
  mailStatus: {
    sent: 'Request e-mailed to the studio inbox, confirmation to the customer',
    failed: 'E-mail not sent (error) — contact the customer by hand',
    off: 'Server e-mail not set up yet — contact the customer by hand',
  },
  alertGo: 'On my way',
  alertVideo: 'A client needs help at the kiosk — the operator did not answer',
  alertOperator: 'The operator asks for a therapist at the kiosk',
  kindProduct: 'Product',
  kindGift: 'Gift card',
  desk: 'Front desk',
  sale: 'Sale',
  crmSyncOk: 'Performed services saved in Altegio',
  crmSyncFailed: 'Altegio not updated — fix by hand',
  nif: 'NIF for the invoice (optional)',
  formOk: 'Form confirmed',
  formNeeded: 'Give the client the form',
  formSubmitted: 'Form filled in at the kiosk',
  formOpen: 'Show health form',
  formHide: 'Hide form',
  formNone: 'No kiosk form on file for this client.',
  formFilled: (day: string) => `Filled in on ${day}`,
  formEarlier: 'from an earlier visit',
  formAttention: (n: number) => `Attention: ${n} “yes” ${n === 1 ? 'answer' : 'answers'} — check before the treatment`,
  formAllNo: 'All answers “no”.',
  cardRegular: 'Regular client',
  cardNew: 'First visit',
  cardNewHint: 'Explain the treatment, show the result and invite them back.',
  cardVisits: (n: number, min: boolean, since?: string) =>
    `${n}${min ? '+' : ''} ${n === 1 ? 'visit' : 'visits'}${since ? ` since ${since}` : ''}`,
  cardLast: 'Last treatments',
  cardNotes: 'Notes',
  cardPressure: 'Preferred pressure',
  cardNoHistory: 'Altegio history unavailable right now',
  cardClientNote: 'Notes about the client (Altegio)',
  cardShowAll: (n: number) => `Show all notes (${n})`,
  cardShowLess: 'Show less',
  cardAddNote: '+ Note about the client',
  cardAddNoteHint: 'Added to the end of the client note in Altegio. Only the team sees it; the client does not.',
  cardAddNoteSave: 'Save note',
  cardAddNoteCancel: 'Cancel',
  cardAddNoteError: 'Could not save the note. Try again or ask the administrator.',
  // treatment protocol (owner 2.11.2026)
  // protocol history, repeat, paper protocol (owner 04.11.2026)
  protoHistory: 'Protocol history',
  protoHistoryEmpty: 'No earlier protocols yet.',
  protoRepeat: '↺ Same as last time',
  protoRepeatFrom: (date: string, who?: string) => `Copied from the protocol of ${date}${who ? ` · ${who}` : ''}`,
  protoPaper: '+ Paper protocol',
  protoPaperTitle: 'Paper protocol (earlier visit)',
  protoPaperDate: 'Visit date',
  protoPaperWho: 'Therapist (from the paper)',
  protoPaperBadge: 'paper',
  protoPaperNote: 'Stays on the client card only. Not sent to the client.',
  protoPaperNeed: 'Pick the date and at least one item',
  protoPaperRemove: 'Remove',
  protoPaperRemoveAsk: 'Remove this paper protocol?',
  protoComment: 'Comment',
  // hot windows (owner 04.11.2026)
  hotOpen: '🔥 Free times',
  hotTitle: 'Today’s free times → notification',
  hotService: 'Treatment',
  hotTimes: 'Pick up to 4 times',
  hotNone: 'No free times left today for this treatment.',
  hotAudience: (w: number, all: number) =>
    `Will get it: ${w} ${w === 1 ? 'client' : 'clients'}${all > w ? ` (${all - w} already got one today)` : ''}`,
  hotPreview: 'The client sees',
  hotOff: 'Notifications are not switched on on the server yet (Apple / Firebase keys missing).',
  hotSend: 'Send notification',
  hotSent: (n: number) => `Sent to ${n} ${n === 1 ? 'phone' : 'phones'} ✓`,
  hotRule: 'Only to clients who asked for «free times» in the app. At most once a day.',
  recoPush: '📲 Send to the app',
  recoPushSent: (n: number) => `Notification sent (${n}) ✓`,
  recoPushAlready: 'Already sent to the app ✓',
  recoPushNone: 'The client has no app with notifications on.',
  protoPick: 'Choose…',
  protoButton: 'Protocol & recommendations',
  protoTitle: 'Treatment protocol',
  protoStudio: 'For the studio (the client does not see it)',
  protoClient: 'For the client',
  protoMask: 'Face mask',
  protoAmpoule: 'Ampoule',
  protoOther: 'Other',
  protoType: 'Treatment type',
  protoOwn: 'Your own recommendation (optional)',
  protoNeed: 'Pick something for the client or write a recommendation',
  recoButton: 'Recommendations for the client',
  recoTitle: 'Recommendations for the client',
  recoHint: 'You can fill this in at the end of the shift. The client gets it by e-mail tomorrow at 11:00.',
  recoText: 'Recommendations (home care, what you noticed, what to do next time)',
  recoProducts: 'Recommended products',
  recoWeeks: 'Come back in',
  recoWeeksN: (n: number) => `${n} wk`,
  recoSend: 'E-mail it to the client tomorrow at 11:00',
  recoNoEmail: 'The client has no e-mail — kept as a note for therapists',
  recoSave: 'Save',
  recoEdit: 'Edit',
  recoMissing: 'No recommendations',
  recoMissingCount: (n: number) => `Recommendations to write: ${n}`,
  recoStatus: {
    scheduled: 'Will be e-mailed tomorrow at 11:00',
    sent: 'Sent to the client',
    failed: 'Sending failed — will retry',
    no_email: 'No e-mail — kept as a note',
    not_sent: 'Note only (not sent)',
    expired: 'Not sent (too late)',
  } as Record<string, string>,
  formAlert: '⚠ The client ticked something on the form — open it before the treatment',
  formNoneDeclared: 'Contraindications: none (confirmed by the client)',
  formIpadDeclared: 'iPad form: the client said something on the list applies — ask what before the treatment',
  formIpadCleared:
    'iPad form: something on the list applies, but the client has medical clearance — confirm before the treatment',
  formIpadAesthetic: 'Recent aesthetic procedures (botox, fillers, threads or surgery) — ask which areas and when',
  formFromIpad: 'filled in on the iPad',
  formDeclared: 'The client ticked:',
  formAesthetic: 'Aesthetic procedures (6 months):',
  formOther: 'Other notes:',
  formDetailDeleted: 'The client ticked something that day — the details were already deleted (kept on that day only)',
  formDeletesAt: 'Health details are deleted tonight at midnight',
  formBirth: 'Born',
  formPressure: 'Pressure',
  formContact: 'Contact',
  formHeard: 'Heard via',
  formSimilar: 'Had similar treatments before',
  formSignature: 'Signature',
  formDecide: 'Therapist decision',
  formProceed: 'Do the treatment',
  formDecline: 'Do not treat',
  formDeclineConfirm:
    'Not treat? The visit closes without services and without payment, noted as “medical reasons — therapist’s decision”.',
  formDeclineYes: 'Yes, close without treatment',
  formDeclineCancel: 'Back',
  medicalDeclined: 'Declined for medical reasons — therapist’s decision. Visit closed without services, €0.',
  medicalDeclineCrm: (s: 'ok' | 'failed' | 'manual'): string =>
    s === 'ok'
      ? 'Altegio: €0 and note recorded'
      : s === 'failed'
        ? 'Altegio: not updated — mark it by hand'
        : 'Altegio: mark by hand (€0, medical reasons)',
  formDecided: (d: 'proceed' | 'decline') =>
    d === 'proceed' ? 'Decision: do the treatment' : 'Decision: do not treat',
  contactLabels: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Phone call' } as Record<
    string,
    string
  >,
  heardLabels: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'AI assistant (ChatGPT, Claude…)',
    friend: 'Recommendation',
    classpass: 'ClassPass',
    passing_by: 'Walked past',
    other: 'Other',
  } as Record<string, string>,
  formYes: 'Yes',
  formNo: 'No',
  formDraft: 'Provisional questions',
  formLogged: 'Health data — every view is logged. Do not copy or photograph.',
  saleInfo: 'Kiosk sale — record it in Altegio after payment',
  handOver: 'Hand over the gift card',
  giftCode: 'Certificate number — scan the barcode',
  presentLabel: 'Gift from us',
  presentPrepare: (gift: string) => `Prepare: ${gift}`,
  giftHandedTakeHome: 'I gave the gift to the client',
  giftHandedDone: 'I did the gift for the client',
  giftHandedFirst: 'Give the gift and tick it — the visit cannot be finished without it.',
  giftHandedDoneShort: 'given ✓',
  nextTitle: 'On the way out',
  nextRebook: 'Book again',
  nextRebookSub: 'Free times with this therapist',
  nextProducts: 'Products',
  nextProductsSub: 'To continue at home',
  nextTreatment: 'Treatment',
  nextNoDates: 'No free times in the coming days — book at the desk.',
  nextPickTime: 'Pick the day and time',
  nextConfirm: (when: string) => `Book for ${when}`,
  nextBooked: (when: string) => `Next visit booked: ${when}`,
  nextRequested: 'Request recorded — enter it in Altegio',
  nextInCrm: 'Booked in Altegio',
  nextAdd: 'Add',
  nextAdded: 'On the bill',
  nextSkip: 'Not now',
  pinTitle: 'Enter your PIN',
  enter: 'Enter',
  logout: 'Log out',
  today: 'Today',
  tomorrow: 'Tomorrow',
  prevDay: 'Previous day',
  nextDay: 'Next day',
  all: 'All',
  visitsCount: (n: number) => `${n} ${n === 1 ? 'visit' : 'visits'}`,
  empty: 'No visits on this day.',
  back: '← List',
  states: {
    booked: 'Booked',
    checked_in: 'Arrived',
    in_service: 'In treatment',
    completed: 'Done — to be paid',
    closed: 'Paid and closed',
    cancelled: 'Cancelled',
    no_show: 'No-show',
  },
  booking: {
    requested: 'Kiosk request — enter it in Altegio',
    crm_booked: 'Created in Altegio',
    from_crm: 'Altegio booking',
  },
  methods: {
    card_terminal: 'Card',
    mbway: 'MB WAY',
    cash: 'Cash',
    bank_transfer: 'Bank transfer',
    gift_card: 'Gift card',
    qr_transfer: 'QR (Wise/bunq/Revolut)',
  },
  giftLookup: 'Check',
  giftValue: (value: string, balance: string) => `Value ${value} · balance ${balance}`,
  giftCoversAll: (left: string) => `The gift card covers the whole bill. Left on the card: ${left}.`,
  giftPartial: (covers: string, missing: string) =>
    `Gift card covers ${covers}, missing ${missing} — choose how the client pays the rest.`,
  giftUsedUp: 'This gift card has been fully used (balance 0).',
  giftNotFound: 'Gift card not found.',
  giftRegister: 'Register this certificate',
  giftRegisterValue: 'Certificate value (€)',
  giftRegisterHint: 'For older or paper certificates. Enter the PIN below.',
  preferredPayment: 'Preferred payment',
  arrived: 'Client arrived',
  start: 'Start treatment',
  noShow: 'No-show',
  cancel: 'Cancel',
  finish: 'Finish treatment',
  performedTitle: 'What was done',
  addService: '+ Add an extra',
  noTime: (extra: number, free: number, next: string) =>
    `Note: +${extra} min, but only ${free} min are free before the ${next} booking (break included).`,
  toPay: 'To pay',
  save: 'Save and finish',
  payTitle: 'Payment received',
  payHint: 'Confirm only after you have received the money. No payment is processed here.',
  pin: 'PIN',
  confirmPay: 'Confirm payment',
  ordered: 'Ordered',
  performed: 'Done',
  paid: 'Paid',
  total: 'Total',
  diff: {
    ordered_not_performed: 'ordered, not done',
    performed_not_ordered: 'done, not ordered',
    performed_not_paid: 'done, not paid',
    paid_not_performed: 'paid, not done',
  },
  certs: 'Gift cards',
  certsTitle: 'Gift certificates',
  certsAltegio: 'Enter in Altegio',
  certsAltegioHint:
    'Altegio does not accept gift card sales through its API. Repeat each line in Altegio and tap «Entered».',
  certsAltegioEmpty: 'Everything is entered in Altegio.',
  certsSale: 'Sale',
  certsUse: 'Payment',
  certsSaleHow: 'Altegio: sell a «Gift card» of this value, code = this number, link it to the client.',
  certsUseHow: 'Altegio: in the visit → Split payment → «Gift card» → this number → this amount.',
  certsBalance: (m: string) => `balance ${m}`,
  certsUntil: (d: string) => `valid until ${d}`,
  certsMarked: 'Entered in Altegio',
  certsStock: 'Printed stock',
  certsValue: 'Value',
  certsInStock: 'On the shelf',
  certsSold: 'Sold',
  certsNewBatch: 'Print new numbers',
  certsCount: 'How many of each value',
  certsCreate: 'Create numbers',
  certsBatches: 'Batches',
  certsPrint: 'Print',
  certsPrintNow: 'Print / save PDF',
  certsBatchLeft: (n: number) => `${n} not sold`,
  certsAltegioStockHint: (n: number) =>
    `In Altegio: one gift card type per value (multiple use, 12 months from sale) and ${n} of each in stock.`,
  printGift: 'Certificado de oferta',
  printGiftEn: 'Gift certificate',
  printValid: (months: number) =>
    `Válido ${months} meses a partir da data de compra · Valid ${months} months from purchase`,
  printFor: 'Para / For',
  printFrom: 'De / From',
  printNo: 'N.º',
  giftStock: 'This number has not been sold yet.',
  giftUntil: (d: string) => `Valid until ${d}.`,
  tipTitle: 'Tip',
  tipHint: 'If the client left a tip (e.g. on the card terminal), enter the amount.',
  tipAmount: 'Tip amount, €',
  discountTitle: 'Discount',
  discountAmount: 'Other amount, €',
  discountReason: 'Reason (required)',
  discountReasonPick: '— choose the reason —',
  discountNote: 'Comment',
  discountNoteRequired: 'Comment (required)',
  discountNone: 'No discount',
  discountTooHigh: 'The discount cannot be bigger than the bill.',
  reasons: {
    therapist_late: 'Therapist was late',
    shorter_service: 'Treatment was shorter',
    therapist_fault: "Therapist's fault",
    management: 'Requested by management',
    voucher: 'Voucher',
    other: 'Other reason',
  } as Record<string, string>,
  payBill: 'Bill',
  payDue: 'To charge',
  payTip: 'Tip',
  payCharge: 'Total to receive',
  discountCrm: {
    ok: 'in Altegio ✓',
    failed: 'Altegio: error — enter by hand',
    manual: 'enter in Altegio by hand',
  } as Record<string, string>,
  report: 'Report',
  reportTitle: (d: string) => `Day report ${d}`,
  reportDiscounts: 'Discounts',
  reportTips: 'Tips',
  reportNone: 'Nothing on this day.',
  reportByReason: 'By reason',
  changeAmount: 'Change amount',
  adjustNone: 'No change',
  adjustDiscount: 'Discount',
  adjustUpgrade: 'Upgrade',
  finalAmount: 'Final amount, €',
  upgradeHint: 'Enter the amount the client pays instead of the bill.',
  payFinal: 'Final amount',
  reportUpgrades: 'Upgrades (final amount)',
  changeTreatment: 'Change treatment',
  addExtras: 'Add extras',
  addExtraAfter: 'Add extra',
  pickTreatment: (from: string) => `Instead of «${from}»:`,
  pickExtras: 'Choose the extras:',
  applyChange: 'Apply',
  closePicker: 'Close',
  changedTo: 'changed to',
  extraTime: (min: number) => `+${min} min`,
  shopButton: 'Sell a product',
  shopSub: 'Show and sell cosmetics',
  shopTitle: 'Sell a product',
  shopSearch: 'Search by name or scan the barcode',
  shopInStock: (n: number) => `in stock: ${n}`,
  shopCart: 'Basket',
  shopCartEmpty: 'Tap a product or scan its barcode.',
  shopChangePrice: 'Change price',
  shopNewPrice: 'New price, €',
  shopMinPrice: (m: string) => `lowest ${m} without the administrator`,
  shopReason: 'Reason / client name',
  shopReasonRequired: 'Reason / client name (required with a discount)',
  shopSeller: 'Who sells',
  shopMethod: 'How the client paid',
  shopFee: (p: string) => `+${p} % payment fee`,
  shopTotal: 'To collect',
  shopSold: 'Sold',
  shopConfirmTitle: 'Confirm the sale',
  shopConfirmBack: 'Back',
  shopDoneTitle: 'Done',
  shopDoneText: (m: string) => `Tell the administrator: issue a receipt for ${m}.`,
  shopNewSale: 'New sale',
  shopNotFound: (code: string) => `Code ${code} not found.`,
  shopDetails: 'Details',
  shopAdd: 'Add',
  shopOverLimit: 'Discount above the limit — only with the administrator.',
  shopRejectedTitle: 'The accounting system did not accept the sale:',
  nextShopProducts: 'Products for sale',
  nextShopProductsSub: 'From the studio stock · paid with the treatment',
  nextShopEmpty: 'No products in stock at this studio.',
  shopSaleOk: (m: string) => `Products booked in the accounting system (${m}). Issue the receipt.`,
  shopSaleFailed: 'Products not booked in the accounting system yet.',
  shopSaleManual: 'Paid with a gift certificate — book the products by hand in the accounting system.',
  shopSaleRetry: 'Try again',
  errors: {
    NOTE_ONLY: 'Marked «note only» — not sent.',
    PUSH_DISABLED: 'Notifications are not switched on yet.',
    NOT_IN_STOCK: 'No more in stock.',
    PRODUCT_UNAVAILABLE: 'Product not available.',
    UCHET_UNAVAILABLE: 'The accounting system is not answering. The basket is kept — try again.',
    UCHET_AUTH: 'The accounting system refused access. Tell the administrator.',
    UCHET_DISABLED: 'Product sales are not switched on.',
    UCHET_REJECTED: 'The sale was not accepted.',
    SELLER_UNKNOWN: 'This therapist is not linked in the accounting system.',
    METHOD_UNKNOWN: 'Payment method not set up.',
    DISCOUNT_REASON_REQUIRED: 'Enter the reason for the discount.',
    DISCOUNT_OVER_LIMIT: 'Discount above the limit — only with the administrator.',
    CHANGE_ONLY_BEFORE_FINISH: 'The treatment can only be changed before finishing.',
    UNKNOWN_SERVICE: 'Service not available.',
    ONE_AMOUNT_CHANGE: 'Choose a discount or an upgrade, not both.',
    DISCOUNT_TOO_HIGH: 'The discount cannot be bigger than the bill.',
    DISCOUNT_NOT_ALLOWED: 'A gift certificate is sold at its full value, without a discount.',
    GIFT_CODE_INVALID: 'Wrong number — check the digits.',
    GIFT_VALUE_MISMATCH: 'This number is for another value.',
    GIFT_NOT_IN_STOCK: 'Hub never printed this number — check the digits.',
    GIFT_NOT_SOLD: 'This certificate has not been sold yet.',
    GIFT_EXPIRED: 'This certificate has expired.',
    UNKNOWN_GIFT_VALUE: 'Value not offered at this studio.',
    PIN_INVALID: 'Wrong PIN.',
    PIN_LOCKED: 'Too many attempts. Wait 15 minutes.',
    TABLET_DISABLED: 'The tablet is not activated on the server yet.',
    INVALID_TRANSITION: 'This action is not possible in the current state.',
    CONCURRENT_UPDATE: 'Another device changed this visit. Refresh.',
    NETWORK: 'No connection. Try again.',
    GIFT_CODE_REQUIRED: 'Enter the certificate number.',
    GIFT_NOT_FOUND: 'Gift card not found.',
    GIFT_USED_UP: 'This gift card has been fully used.',
    GIFT_CODE_EXISTS: 'This certificate number already exists.',
    GIFT_ONE_PER_CODE: 'One number = one certificate. Record each envelope separately.',
    REMAINDER_METHOD_REQUIRED: 'The gift card does not cover everything — choose how to pay the rest.',
    QUESTIONNAIRE_UNREADABLE: 'The form cannot be read (server key). Ask the client for a paper form.',
    default: 'Error. Try again.',
  },
};

const ru: TabletMessages = {
  langName: 'RU',
  alert: (mins: number) => `Клиент позвал сотрудника у киоска${mins > 0 ? ` (${mins} мин назад)` : ''}`,
  alertGiftEmail: 'Новая заявка на сертификат по e-mail — смотрите почту info@360face.pt',
  deliveryEmail: 'По почте',
  deliveryPickup: 'Забирает в студии',
  clientComment: 'Комментарий клиента',
  deliveryEmailNote: 'Сертификат по e-mail — заявкой занимается администратор через info@360face.pt',
  cardFor: 'Кому',
  cardFrom: 'От кого',
  mailStatus: {
    sent: 'Заявка отправлена на почту студии, клиенту — подтверждение',
    failed: 'Письмо не отправлено (ошибка) — связаться с клиентом вручную',
    off: 'Почта сервера ещё не настроена — связаться с клиентом вручную',
  },
  alertGo: 'Иду',
  alertVideo: 'Клиенту нужна помощь у киоска — оператор не ответил',
  alertOperator: 'Оператор просит подойти к киоску',
  kindProduct: 'Товар',
  kindGift: 'Сертификат',
  desk: 'Ресепшн',
  sale: 'Продажа',
  crmSyncOk: 'Выполненные услуги записаны в Altegio',
  crmSyncFailed: 'Altegio не обновлён — поправить вручную',
  nif: 'NIF для фатуры (необязательно)',
  formOk: 'Анкета подтверждена',
  formNeeded: 'Дать клиенту анкету',
  formSubmitted: 'Анкета заполнена в киоске',
  formOpen: 'Показать анкету здоровья',
  formHide: 'Скрыть анкету',
  formNone: 'Анкеты из киоска для этого клиента нет.',
  formFilled: (day: string) => `Заполнена ${day}`,
  formEarlier: 'с прошлого визита',
  formAttention: (n: number) => `Внимание: ответов «да» — ${n}. Проверьте перед процедурой`,
  formAllNo: 'Все ответы «нет».',
  cardRegular: 'Постоянный клиент',
  cardNew: 'Первый визит',
  cardNewHint: 'Расскажите о процедуре, покажите результат и пригласите снова.',
  cardVisits: (n: number, min: boolean, since?: string) =>
    `${n}${min ? '+' : ''} ${n === 1 ? 'визит' : n < 5 ? 'визита' : 'визитов'}${since ? ` с ${since}` : ''}`,
  cardLast: 'Последние процедуры',
  cardNotes: 'Заметки',
  cardPressure: 'Предпочитаемый нажим',
  cardNoHistory: 'История из Altegio сейчас недоступна',
  cardClientNote: 'Заметки о клиенте (Altegio)',
  cardShowAll: (n: number) => `Все заметки (${n})`,
  cardShowLess: 'Свернуть',
  cardAddNote: '+ Заметка о клиенте',
  cardAddNoteHint: 'Добавится в конец заметки клиента в Altegio. Видит только команда, клиенту не отправляется.',
  cardAddNoteSave: 'Сохранить заметку',
  cardAddNoteCancel: 'Отмена',
  cardAddNoteError: 'Не удалось сохранить заметку. Попробуйте ещё раз или обратитесь к администратору.',
  // treatment protocol (owner 2.11.2026)
  // protocol history, repeat, paper protocol (owner 04.11.2026)
  protoHistory: 'История протоколов',
  protoHistoryEmpty: 'Прошлых протоколов пока нет.',
  protoRepeat: '↺ Как в прошлый раз',
  protoRepeatFrom: (date: string, who?: string) => `Скопировано из протокола ${date}${who ? ` · ${who}` : ''}`,
  protoPaper: '+ Бумажный протокол',
  protoPaperTitle: 'Бумажный протокол (прошлый визит)',
  protoPaperDate: 'Дата визита',
  protoPaperWho: 'Мастер (из бумаги)',
  protoPaperBadge: 'бумага',
  protoPaperNote: 'Хранится только в карточке клиента. Клиенту не отправляется.',
  protoPaperNeed: 'Укажите дату и хотя бы один пункт',
  protoPaperRemove: 'Удалить',
  protoPaperRemoveAsk: 'Удалить этот бумажный протокол?',
  protoComment: 'Комментарий',
  // hot windows (owner 04.11.2026)
  hotOpen: '🔥 Горящие окна',
  hotTitle: 'Свободное время сегодня → уведомление',
  hotService: 'Процедура',
  hotTimes: 'Выберите до 4 времён',
  hotNone: 'На сегодня свободного времени для этой процедуры нет.',
  hotAudience: (w: number, all: number) => `Получат: ${w}${all > w ? ` (${all - w} уже получили сегодня)` : ''}`,
  hotPreview: 'Клиент увидит',
  hotOff: 'Уведомления на сервере ещё не включены (нет ключей Apple / Firebase).',
  hotSend: 'Отправить уведомление',
  hotSent: (n: number) => `Отправлено на ${n} тел. ✓`,
  hotRule: 'Только клиентам, которые включили «горящие окна» в приложении. Не чаще раза в день.',
  recoPush: '📲 Отправить в приложение',
  recoPushSent: (n: number) => `Уведомление отправлено (${n}) ✓`,
  recoPushAlready: 'Уже отправлено в приложение ✓',
  recoPushNone: 'У клиента нет приложения с включёнными уведомлениями.',
  protoPick: 'Выбрать…',
  protoButton: 'Протокол и рекомендации',
  protoTitle: 'Протокол процедуры',
  protoStudio: 'Для студии (клиент не видит)',
  protoClient: 'Для клиента',
  protoMask: 'Маска',
  protoAmpoule: 'Ампула',
  protoOther: 'Другое',
  protoType: 'Тип процедуры',
  protoOwn: 'Ваша рекомендация (необязательно)',
  protoNeed: 'Выберите что-то для клиента или напишите рекомендацию',
  recoButton: 'Рекомендации для клиента',
  recoTitle: 'Рекомендации для клиента',
  recoHint: 'Можно заполнить в конце смены. Клиент получит их по e-mail завтра в 11:00.',
  recoText: 'Рекомендации (уход дома, что заметили, что делать в следующий раз)',
  recoProducts: 'Рекомендованные средства',
  recoWeeks: 'Прийти снова через',
  recoWeeksN: (n: number) => `${n} нед.`,
  recoSend: 'Отправить клиенту на e-mail завтра в 11:00',
  recoNoEmail: 'У клиента нет e-mail — останется заметкой для мастеров',
  recoSave: 'Сохранить',
  recoEdit: 'Изменить',
  recoMissing: 'Нет рекомендаций',
  recoMissingCount: (n: number) => `Рекомендации не заполнены: ${n}`,
  recoStatus: {
    scheduled: 'Уйдёт клиенту на e-mail завтра в 11:00',
    sent: 'Отправлено клиенту',
    failed: 'Ошибка отправки — повторим',
    no_email: 'Нет e-mail — сохранено как заметка',
    not_sent: 'Только заметка (не отправлялась)',
    expired: 'Не отправлено (срок прошёл)',
  } as Record<string, string>,
  formAlert: '⚠ Клиент отметил что-то в анкете — откройте до процедуры',
  formNoneDeclared: 'Противопоказаний нет (подтверждено клиентом)',
  formIpadDeclared:
    'Анкета с iPad: клиент отметил, что что-то из списка относится к нему — спросите, что именно, до процедуры',
  formIpadCleared: 'Анкета с iPad: что-то из списка относится, но есть разрешение врача — уточните до процедуры',
  formIpadAesthetic: 'Недавние эстетические процедуры (ботокс, филлеры, нити или операция) — спросите зоны и сроки',
  formFromIpad: 'заполнена на iPad',
  formDeclared: 'Клиент отметил:',
  formAesthetic: 'Эстетические процедуры (6 мес.):',
  formOther: 'Другое:',
  formDetailDeleted: 'В тот день клиент что-то отмечал — подробности уже удалены (хранятся только в день визита)',
  formDeletesAt: 'Подробности о здоровье удаляются сегодня в полночь',
  formBirth: 'Дата рождения',
  formPressure: 'Нажим',
  formContact: 'Связь',
  formHeard: 'Узнал(а) через',
  formSimilar: 'Похожие процедуры раньше',
  formSignature: 'Подпись',
  formDecide: 'Решение мастера',
  formProceed: 'Проводим процедуру',
  formDecline: 'Не проводим',
  formDeclineConfirm:
    'Не проводить процедуру? Визит закроется без услуг и без оплаты, с пометкой «по медицинским показаниям — решение мастера».',
  formDeclineYes: 'Да, закрыть без процедуры',
  formDeclineCancel: 'Назад',
  medicalDeclined: 'Отказ по медицинским показаниям — решение мастера. Визит закрыт без услуг, 0 €.',
  medicalDeclineCrm: (s: 'ok' | 'failed' | 'manual'): string =>
    s === 'ok'
      ? 'Altegio: 0 € и пометка записаны'
      : s === 'failed'
        ? 'Altegio: не обновился — отметьте вручную'
        : 'Altegio: отметьте вручную (0 €, мед. показания)',
  formDecided: (d: 'proceed' | 'decline') => (d === 'proceed' ? 'Решение: проводим' : 'Решение: не проводим'),
  contactLabels: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Звонок' } as Record<
    string,
    string
  >,
  heardLabels: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'ИИ-ассистент (ChatGPT, Claude…)',
    friend: 'Рекомендация',
    classpass: 'ClassPass',
    passing_by: 'Проходил(а) мимо',
    other: 'Другое',
  } as Record<string, string>,
  formYes: 'Да',
  formNo: 'Нет',
  formDraft: 'Предварительные вопросы',
  formLogged: 'Данные о здоровье — каждый просмотр записывается. Не копировать и не фотографировать.',
  saleInfo: 'Продажа с киоска — внести в Altegio после оплаты',
  handOver: 'Выдать сертификат',
  giftCode: 'Номер сертификата — отсканируйте штрихкод',
  presentLabel: 'Подарок от студии',
  presentPrepare: (gift: string) => `Подготовить: ${gift}`,
  giftHandedTakeHome: 'Я вручила подарок клиентке',
  giftHandedDone: 'Я сделала подарок клиентке',
  giftHandedFirst: 'Вручите подарок и отметьте — без этого визит не завершить.',
  giftHandedDoneShort: 'вручён ✓',
  nextTitle: 'На выходе',
  nextRebook: 'Записать снова',
  nextRebookSub: 'Свободные окна этого мастера',
  nextProducts: 'Товары',
  nextProductsSub: 'Для ухода дома',
  nextTreatment: 'Процедура',
  nextNoDates: 'Свободных окон в ближайшие дни нет — записать на ресепшене.',
  nextPickTime: 'Выберите день и время',
  nextConfirm: (when: string) => `Записать на ${when}`,
  nextBooked: (when: string) => `Следующий визит записан: ${when}`,
  nextRequested: 'Заявка записана — внести в Altegio',
  nextInCrm: 'Записано в Altegio',
  nextAdd: 'Добавить',
  nextAdded: 'В чеке',
  nextSkip: 'Не сейчас',
  pinTitle: 'Введите PIN',
  enter: 'Войти',
  logout: 'Выйти',
  today: 'Сегодня',
  tomorrow: 'Завтра',
  prevDay: 'Предыдущий день',
  nextDay: 'Следующий день',
  all: 'Все',
  visitsCount: (n: number) => `${n} ${plural(n, 'визит', 'визита', 'визитов')}`,
  empty: 'В этот день визитов нет.',
  back: '← Список',
  states: {
    booked: 'Записан',
    checked_in: 'Пришёл',
    in_service: 'На процедуре',
    completed: 'Готово — к оплате',
    closed: 'Оплачено и закрыто',
    cancelled: 'Отменён',
    no_show: 'Не пришёл',
  },
  booking: {
    requested: 'Заявка с киоска — внести в Altegio',
    crm_booked: 'Создано в Altegio',
    from_crm: 'Запись из Altegio',
  },
  methods: {
    card_terminal: 'Карта',
    mbway: 'MB WAY',
    cash: 'Наличные',
    bank_transfer: 'Перевод',
    gift_card: 'Сертификат',
    qr_transfer: 'QR (Wise/bunq/Revolut)',
  },
  giftLookup: 'Проверить',
  giftValue: (value: string, balance: string) => `Номинал ${value} · остаток ${balance}`,
  giftCoversAll: (left: string) => `Сертификат покрывает весь счёт. Останется на сертификате: ${left}.`,
  giftPartial: (covers: string, missing: string) =>
    `Сертификат покрывает ${covers}, не хватает ${missing} — выберите, чем клиент доплатит.`,
  giftUsedUp: 'Сертификат уже полностью использован (остаток 0).',
  giftNotFound: 'Сертификат не найден.',
  giftRegister: 'Зарегистрировать этот сертификат',
  giftRegisterValue: 'Номинал сертификата (€)',
  giftRegisterHint: 'Для старых или бумажных сертификатов. Введите PIN ниже.',
  preferredPayment: 'Желаемая оплата',
  arrived: 'Клиент пришёл',
  start: 'Начать процедуру',
  noShow: 'Не пришёл',
  cancel: 'Отменить',
  finish: 'Завершить процедуру',
  performedTitle: 'Что сделано',
  addService: '+ Добавить доп. услугу',
  noTime: (extra: number, free: number, next: string) =>
    `Внимание: +${extra} мин, а до записи в ${next} свободно только ${free} мин (с учётом перерыва).`,
  toPay: 'К оплате',
  save: 'Сохранить и завершить',
  payTitle: 'Оплата получена',
  payHint: 'Подтверждайте только после получения денег. Здесь платежи не проводятся.',
  pin: 'PIN',
  confirmPay: 'Подтвердить оплату',
  ordered: 'Заказано',
  performed: 'Сделано',
  paid: 'Оплачено',
  total: 'Итого',
  diff: {
    ordered_not_performed: 'заказано, не сделано',
    performed_not_ordered: 'сделано без заказа',
    performed_not_paid: 'сделано, не оплачено',
    paid_not_performed: 'оплачено, не сделано',
  },
  certs: 'Сертификаты',
  certsTitle: 'Подарочные сертификаты',
  certsAltegio: 'Внести в Altegio',
  certsAltegioHint:
    'Altegio не принимает продажу сертификатов через API. Повторите каждую строку в Altegio и нажмите «Внесено».',
  certsAltegioEmpty: 'Всё внесено в Altegio.',
  certsSale: 'Продажа',
  certsUse: 'Оплата',
  certsSaleHow: 'Altegio: продать «Gift card» этого номинала, код = этот номер, привязать к клиенту.',
  certsUseHow: 'Altegio: в визите → Split payment → «Gift card» → этот номер → эта сумма.',
  certsBalance: (m: string) => `остаток ${m}`,
  certsUntil: (d: string) => `действует до ${d}`,
  certsMarked: 'Внесено в Altegio',
  certsStock: 'Напечатанные',
  certsValue: 'Номинал',
  certsInStock: 'На полке',
  certsSold: 'Продано',
  certsNewBatch: 'Напечатать новые номера',
  certsCount: 'Сколько каждого номинала',
  certsCreate: 'Создать номера',
  certsBatches: 'Партии',
  certsPrint: 'Печать',
  certsPrintNow: 'Печать / сохранить PDF',
  certsBatchLeft: (n: number) => `${n} не продано`,
  certsAltegioStockHint: (n: number) =>
    `В Altegio: тип сертификата на каждый номинал (многоразовый, 12 месяцев с продажи) и ${n} шт. каждого на складе.`,
  printGift: 'Certificado de oferta',
  printGiftEn: 'Gift certificate',
  printValid: (months: number) =>
    `Válido ${months} meses a partir da data de compra · Valid ${months} months from purchase`,
  printFor: 'Para / For',
  printFrom: 'De / From',
  printNo: 'N.º',
  giftStock: 'Этот номер ещё не продан.',
  giftUntil: (d: string) => `Действует до ${d}.`,
  tipTitle: 'Чаевые',
  tipHint: 'Если клиент оставил чаевые (например, по карте на терминале), введите сумму.',
  tipAmount: 'Сумма чаевых, €',
  discountTitle: 'Скидка',
  discountAmount: 'Другая сумма, €',
  discountReason: 'Причина (обязательно)',
  discountReasonPick: '— выберите причину —',
  discountNote: 'Комментарий',
  discountNoteRequired: 'Комментарий (обязательно)',
  discountNone: 'Без скидки',
  discountTooHigh: 'Скидка не может быть больше счёта.',
  reasons: {
    therapist_late: 'Мастер опоздал',
    shorter_service: 'Услуга была короче',
    therapist_fault: 'По вине мастера',
    management: 'По просьбе руководства',
    voucher: 'Ваучер',
    other: 'Иная причина',
  } as Record<string, string>,
  payBill: 'Счёт',
  payDue: 'К оплате',
  payTip: 'Чаевые',
  payCharge: 'Итого получить',
  discountCrm: {
    ok: 'в Altegio ✓',
    failed: 'Altegio: ошибка — внести вручную',
    manual: 'внести в Altegio вручную',
  } as Record<string, string>,
  report: 'Отчёт',
  reportTitle: (d: string) => `Отчёт за ${d}`,
  reportDiscounts: 'Скидки',
  reportTips: 'Чаевые',
  reportNone: 'За этот день ничего нет.',
  reportByReason: 'По причинам',
  changeAmount: 'Изменить сумму',
  adjustNone: 'Без изменений',
  adjustDiscount: 'Скидка',
  adjustUpgrade: 'Апгрейд',
  finalAmount: 'Итоговая сумма, €',
  upgradeHint: 'Введите сумму, которую клиент платит вместо счёта.',
  payFinal: 'Итоговая сумма',
  reportUpgrades: 'Апгрейды (итоговая сумма)',
  changeTreatment: 'Изменить процедуру',
  addExtras: 'Добавить доп. услуги',
  addExtraAfter: 'Добавить доп. услугу',
  pickTreatment: (from: string) => `Вместо «${from}»:`,
  pickExtras: 'Выберите доп. услуги:',
  applyChange: 'Применить',
  closePicker: 'Закрыть',
  changedTo: 'заменено на',
  extraTime: (min: number) => `+${min} мин`,
  shopButton: 'Продать товар',
  shopSub: 'Предложить и продать косметику',
  shopTitle: 'Продать товар',
  shopSearch: 'Поиск по названию или скан штрихкода',
  shopInStock: (n: number) => `в наличии: ${n}`,
  shopCart: 'Корзина',
  shopCartEmpty: 'Нажмите на товар или отсканируйте штрихкод.',
  shopChangePrice: 'Изменить цену',
  shopNewPrice: 'Новая цена, €',
  shopMinPrice: (m: string) => `минимум ${m} без администратора`,
  shopReason: 'Причина / имя клиента',
  shopReasonRequired: 'Причина / имя клиента (обязательно при скидке)',
  shopSeller: 'Кто продаёт',
  shopMethod: 'Как оплатил клиент',
  shopFee: (p: string) => `+${p} % за способ оплаты`,
  shopTotal: 'К оплате',
  shopSold: 'Продано',
  shopConfirmTitle: 'Подтвердите продажу',
  shopConfirmBack: 'Назад',
  shopDoneTitle: 'Готово',
  shopDoneText: (m: string) => `Передайте администратору: пробить чек на ${m}.`,
  shopNewSale: 'Новая продажа',
  shopNotFound: (code: string) => `Код ${code} не найден.`,
  shopDetails: 'Подробнее',
  shopAdd: 'В корзину',
  shopOverLimit: 'Скидка больше лимита — только через администратора.',
  shopRejectedTitle: 'Учёт не принял продажу:',
  nextShopProducts: 'Товары для продажи',
  nextShopProductsSub: 'Из наличия салона · оплата вместе с процедурой',
  nextShopEmpty: 'В этом салоне нет товаров в наличии.',
  shopSaleOk: (m: string) => `Товары списаны в учёте (${m}). Пробить чек.`,
  shopSaleFailed: 'Товары ещё не списаны в учёте.',
  shopSaleManual: 'Оплачено сертификатом — внести товары в учёт вручную.',
  shopSaleRetry: 'Повторить',
  errors: {
    NOTE_ONLY: 'Отмечено «только заметка» — не отправляется.',
    PUSH_DISABLED: 'Уведомления ещё не включены.',
    NOT_IN_STOCK: 'Больше нет в наличии.',
    PRODUCT_UNAVAILABLE: 'Товар недоступен.',
    UCHET_UNAVAILABLE: 'Учёт не отвечает. Корзина сохранена — попробуйте ещё раз.',
    UCHET_AUTH: 'Учёт отказал в доступе. Сообщите администратору.',
    UCHET_DISABLED: 'Продажа товаров не включена.',
    UCHET_REJECTED: 'Продажа не принята.',
    SELLER_UNKNOWN: 'Этот мастер не привязан в учёте.',
    METHOD_UNKNOWN: 'Способ оплаты не настроен.',
    DISCOUNT_REASON_REQUIRED: 'Укажите причину скидки.',
    DISCOUNT_OVER_LIMIT: 'Скидка больше лимита — только через администратора.',
    CHANGE_ONLY_BEFORE_FINISH: 'Процедуру можно изменить только до завершения.',
    UNKNOWN_SERVICE: 'Услуга недоступна.',
    ONE_AMOUNT_CHANGE: 'Выберите скидку или апгрейд, не оба сразу.',
    DISCOUNT_TOO_HIGH: 'Скидка не может быть больше счёта.',
    DISCOUNT_NOT_ALLOWED: 'Сертификат продаётся по номиналу, без скидки.',
    GIFT_CODE_INVALID: 'Неверный номер — проверьте цифры.',
    GIFT_VALUE_MISMATCH: 'Этот номер другого номинала.',
    GIFT_NOT_IN_STOCK: 'Такой номер Hub не печатал — проверьте цифры.',
    GIFT_NOT_SOLD: 'Этот сертификат ещё не продан.',
    GIFT_EXPIRED: 'Срок сертификата истёк.',
    UNKNOWN_GIFT_VALUE: 'Такого номинала нет в этой студии.',
    PIN_INVALID: 'Неверный PIN.',
    PIN_LOCKED: 'Слишком много попыток. Подождите 15 минут.',
    TABLET_DISABLED: 'Планшет ещё не включён на сервере.',
    INVALID_TRANSITION: 'Это действие сейчас недоступно.',
    CONCURRENT_UPDATE: 'Визит изменён на другом устройстве. Обновите.',
    NETWORK: 'Нет связи. Попробуйте ещё раз.',
    GIFT_CODE_REQUIRED: 'Введите номер сертификата.',
    GIFT_NOT_FOUND: 'Сертификат не найден.',
    GIFT_USED_UP: 'Сертификат уже полностью использован.',
    GIFT_CODE_EXISTS: 'Такой номер сертификата уже есть.',
    GIFT_ONE_PER_CODE: 'Один номер — один сертификат. Внесите каждый конверт отдельно.',
    REMAINDER_METHOD_REQUIRED: 'Сертификат покрывает не всё — выберите, чем оплатить остаток.',
    QUESTIONNAIRE_UNREADABLE: 'Анкету не удаётся прочитать (ключ сервера). Попросите клиента заполнить бумажную.',
    default: 'Ошибка. Попробуйте ещё раз.',
  },
};

function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return one;
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return few;
  return many;
}

export type TabletLocale = 'pt' | 'en' | 'ru';
export const TABLET_LOCALES: TabletLocale[] = ['pt', 'en', 'ru'];
export const TABLET_MESSAGES: Record<TabletLocale, TabletMessages> = { pt, en, ru };

const INTL: Record<TabletLocale, string> = { pt: 'pt-PT', en: 'en-GB', ru: 'ru-RU' };

/** Language for catalogue texts (PT/EN only). */
export function contentLocale(l: TabletLocale): Locale {
  return l === 'pt' ? 'pt' : 'en';
}

/** 'Qua, 14/10' · 'Wed, 14 Oct' · 'Ср, 14 окт.' */
export function tabletDay(isoDate: string, l: TabletLocale): string {
  const [y, mo, d] = isoDate.split('-').map(Number);
  const text = new Intl.DateTimeFormat(INTL[l], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y!, mo! - 1, d!)));
  return text.charAt(0).toLocaleUpperCase(INTL[l]) + text.slice(1);
}

export function tabletMoney(m: Money, l: TabletLocale): string {
  return new Intl.NumberFormat(INTL[l], {
    style: 'currency',
    currency: m.currency,
    currencyDisplay: 'narrowSymbol',
  }).format(m.amountMinor / 100);
}

const KEY = '360face.tablet.locale';

/** Language is a per-device convenience: remembered locally, Portuguese by default. */
export function loadTabletLocale(): TabletLocale {
  try {
    const v = localStorage.getItem(KEY);
    return TABLET_LOCALES.includes(v as TabletLocale) ? (v as TabletLocale) : 'pt';
  } catch {
    return 'pt';
  }
}

export function saveTabletLocale(l: TabletLocale): void {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* not remembered */
  }
}
